import React, { useContext, useEffect, useState } from 'react';
import { ConfigProvider, Modal, Table, TableProps, Tag, Input, Button, Space, Switch, message } from 'antd';
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import BbddColors from "../../../../styles/BbddColors";
import { ColumnsType } from "antd/es/table";
import { GetBbddProblemes } from "../../../../dao/bbdd/GetBbddProblemes";
import { GetUsuariIdActivitatsId } from "../../../../dao/GetUsuariIdActivitatsId";
import { PutBbddActivitatProblemes } from "../../../../dao/bbdd/PutBbddActivitatProblemes";

interface Props {
    nom: string;
    id_activitat: string;
    visible: boolean;
    onClose: (value: boolean) => void;
}

const ExercicisModal: React.FC<Props> = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dades, setDades] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const { isLoggedIn, userId } = useContext(AuthContext);

    useEffect(() => {
        if (isLoggedIn) checkDades();
    }, [isLoggedIn, props.id_activitat]);

    const parseList = (res: any): any[] => {
        if (Array.isArray(res)) return res;
        if (typeof res === 'string') {
            try {
                const parsed = JSON.parse(res);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        if (res && res.json) {
            try {
                const parsed = typeof res.json === 'string' ? JSON.parse(res.json) : res.json;
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    async function checkDades() {
        setLoading(true);
        try {
            const prob = await GetBbddProblemes(userId);
            setDades(parseList(prob));
            const cur = await GetUsuariIdActivitatsId(userId, props.id_activitat + "");
            const currentList = parseList(cur);
            setSelectedIds(
                currentList
                    .filter((a: any) => a && a.id != null)
                    .map((a: any) => Number(a.id))
            );
        } catch (err) {
            console.error("Error al carregar exercicis:", err);
            setDades([]);
            setSelectedIds([]);
        } finally {
            setLoading(false);
        }
    }

    const handleCancel = () => props.onClose(false);

    function marcarEjercicio(record: any, checked: boolean) {
        const idNum = Number(record.id);
        setSelectedIds(prev => {
            if (checked) {
                return prev.includes(idNum) ? prev : [...prev, idNum];
            } else {
                return prev.filter(id => id !== idNum);
            }
        });
    }

    async function handleSave() {
        setSaving(true);
        try {
            await PutBbddActivitatProblemes(props.id_activitat + "", selectedIds);
            message.success("Exercicis de l'activitat guardats correctament");
            props.onClose(true);
        } catch (err) {
            console.error("Error al guardar exercicis:", err);
            message.error("Error al guardar els exercicis");
        } finally {
            setSaving(false);
        }
    }

    const getTipusTagColor = (tipus: string) => {
        switch (tipus?.toUpperCase()) {
            case 'ER': return 'purple';
            case 'SCRIPT': return 'cyan';
            case 'RELACIONAL': return 'blue';
            case 'DDL': return 'geekblue';
            case 'SQL': return 'green';
            case 'PLSQL': return 'volcano';
            case 'DCL': return 'orange';
            case 'NOSQL': return 'magenta';
            default: return 'default';
        }
    };

    const columns: ColumnsType<any> = [
        {
            key: 'id',
            title: 'ID',
            dataIndex: 'id',
            width: 70,
            align: 'center',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => a.id - b.id,
            render: (id) => <Tag color={BbddColors.colors.color5}>#{id}</Tag>
        },
        {
            key: 'nom',
            title: 'Nom',
            dataIndex: 'nom',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Cercar per nom..."
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                            style={{ width: 90, backgroundColor: BbddColors.colors.color3, borderColor: BbddColors.colors.color3 }}
                        >
                            Cercar
                        </Button>
                        <Button
                            onClick={() => clearFilters && clearFilters()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Netejar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => <span>🔍</span>,
            onFilter: (value, record) =>
                (record.nom || record.prac || '').toLowerCase().includes((value as string).toLowerCase()),
            render: (text: string, record: any) => (
                <div>
                    <span style={{ fontWeight: 600 }}>{text || record.prac || `Exercici ${record.id}`}</span>
                    {record.descripcio && (
                        <div style={{ fontSize: '0.8rem', color: '#888', maxWidth: 450 }} className="text-truncate">
                            {record.descripcio}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'tipus',
            title: 'Tipus',
            dataIndex: 'tipus',
            width: 120,
            filters: [
                { text: 'ER', value: 'ER' },
                { text: 'Script', value: 'Script' },
                { text: 'Relacional', value: 'Relacional' },
                { text: 'DDL', value: 'DDL' },
                { text: 'SQL', value: 'SQL' },
                { text: 'PLSQL', value: 'PLSQL' },
                { text: 'DCL', value: 'DCL' },
                { text: 'NoSQL', value: 'NoSQL' },
            ],
            onFilter: (value, record) => String(record.tipus || '').toUpperCase() === String(value).toUpperCase(),
            render: (tipus: string) => (
                <Tag color={getTipusTagColor(tipus)} style={{ fontWeight: 600 }}>
                    {tipus || 'SQL'}
                </Tag>
            )
        },
        {
            key: 'dificultat',
            title: 'Dificultat',
            dataIndex: 'dificultat',
            width: 130,
            filters: [
                { text: 'beginner', value: 'beginner' },
                { text: 'intermediate', value: 'intermediate' },
                { text: 'expert', value: 'expert' },
            ],
            onFilter: (value, record) => record.dificultat === value,
            render: (value: string) => (
                <Tag color={
                    value === 'beginner' ? 'green' :
                        value === 'intermediate' ? 'orange' :
                            'red'
                } style={{ textTransform: 'capitalize' }}>
                    {value || 'beginner'}
                </Tag>
            ),
        },
        {
            key: 'assignat',
            title: 'Assignat',
            dataIndex: 'assignat',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const estaAssignat = selectedIds.includes(Number(record.id));

                return (
                    <Switch
                        checked={estaAssignat}
                        onChange={(checked) => marcarEjercicio(record, checked)}
                    />
                );
            }
        }
    ];

    const tableProps: TableProps<any> = {
        loading,
        pagination: { pageSize: 8 }
    };

    const safeDades = Array.isArray(dades) ? [...dades] : [];

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={"Assignar Exercicis a l'Activitat: " + props.nom}
                open={props.visible}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancelar" onClick={handleCancel} disabled={saving}>
                        Cancel·lar
                    </Button>,
                    <Button
                        key="guardar"
                        type="primary"
                        loading={saving}
                        onClick={handleSave}
                        style={{ backgroundColor: BbddColors.colors.color3, borderColor: BbddColors.colors.color3 }}
                    >
                        Guardar ({selectedIds.length})
                    </Button>
                ]}
                width={1000}
                destroyOnClose
            >
                <div style={{ marginBottom: 12, color: '#666' }}>
                    Activa o desactiva els interruptors dels exercicis que vulguis i prem <b>Guardar</b> per aplicar els canvis.
                </div>
                <Table
                    showHeader={true}
                    size="small"
                    {...tableProps}
                    bordered
                    columns={columns}
                    dataSource={safeDades.sort((a: any, b: any) => a.id - b.id)}
                    rowKey="id"
                />
            </Modal>
        </ConfigProvider>
    );

};

export default ExercicisModal;
